/**
 * @file    ProjectArchModal.js
 * @author  Daniel Naftzger
 * @created 2026-03-05
 * @updated 2026-03-05
 *
 * @description
 * Modal component that displays an architectural overview of a project.
 * Shows system layers with their tech stacks, and a numbered data-flow
 * diagram. Opened from the "About this Project" button on ProjectCard.
 *
 * Only plain inline styles (from styles.js) are used — no MUI dependency,
 * consistent with the rest of the site outside InfoPopup.js.
 *
 * The modal closes on:
 *   - Clicking the ✕ button
 *   - Pressing Escape
 *   - Clicking the backdrop overlay
 */
import { useEffect, useRef, useCallback } from 'react';
import styles from '../styles';

/**
 * Renders a fullscreen overlay modal with the project's architecture info.
 *
 * @param {Object}   props
 * @param {Object}   props.project      - The project object from data.js (must include .architecture)
 * @param {Function} props.onClose      - Callback to close the modal
 * @returns {JSX.Element}
 */
function ProjectArchModal({ project, onClose }) {
  const arch = project.architecture;
  const overlayRef = useRef(null);

  /** Close on Escape key press. */
  const handleKey = useCallback(
    (e) => { if (e.key === 'Escape') onClose(); },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [handleKey]);

  /** Close when clicking the overlay backdrop, not the modal itself. */
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div ref={overlayRef} style={styles.archOverlay} onClick={handleOverlayClick}>
      <div
        className="dn-arch-modal"
        style={styles.archModal}
        role="dialog"
        aria-modal="true"
        aria-label={`Architecture overview: ${project.name}`}
      >
        {/* ── Header ─────────────────────────────────────────── */}
        <div className="dn-arch-modal-header" style={styles.archModalHeader}>
          <div>
            <div style={styles.archModalTitle}>{project.name}</div>
            <div style={styles.archModalYear}>{project.year} — Architecture Overview</div>
          </div>
          <button
            style={styles.archCloseBtn}
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────── */}
        <div className="dn-arch-modal-body" style={styles.archModalBody}>
          {/* Summary */}
          <p style={styles.archSummary}>{arch.summary}</p>

          {/* System Layers */}
          <div>
            <div style={styles.archSectionLabel}>System Layers</div>
            {arch.layers.map((layer, i) => (
              <div key={i} style={styles.archLayerCard}>
                <div style={styles.archLayerHeader}>
                  <span style={styles.archLayerName}>{layer.name}</span>
                  <div style={styles.archLayerTech}>
                    {layer.tech.map((t, j) => (
                      <span key={j} style={styles.archLayerTag}>{t}</span>
                    ))}
                  </div>
                </div>
                <p style={styles.archLayerDetail}>{layer.detail}</p>
              </div>
            ))}
          </div>

          {/* Data Flow */}
          {arch.flow && arch.flow.length > 0 && (
            <div>
              <div style={styles.archSectionLabel}>Data Flow</div>
              <div style={styles.archFlowList}>
                {arch.flow.map((step, i) => (
                  <div key={i} style={styles.archFlowStep}>
                    <span style={styles.archFlowIndex}>{i + 1}</span>
                    {i < arch.flow.length - 1 && <div style={styles.archFlowConnector} />}
                    <span style={styles.archFlowText}>{step}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProjectArchModal;